/**
 * Generated by @openapi-codegen
 *
 * @version v1
 */
import * as reactQuery from '@tanstack/react-query';
import { useConnectorContext, ConnectorContext } from './connector-context';
import type * as Fetcher from './connector-fetcher';
import { connectorFetch } from './connector-fetcher';
import type * as Schemas from './connector-schemas';

export type DownloadDicomSeriesPathParams = {
    studyId: string;
    seriesId: string;
};

export type DownloadDicomSeriesError = Fetcher.ErrorWrapper<undefined>;

export type DownloadDicomSeriesVariables = {
    pathParams: DownloadDicomSeriesPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDownloadDicomSeries = (
    variables: DownloadDicomSeriesVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        DownloadDicomSeriesError,
        undefined,
        {},
        {},
        DownloadDicomSeriesPathParams
    >({
        url: '/api/studies/{studyId}/series/{seriesId}.zip',
        method: 'get',
        ...variables,
        signal,
    });

export const useDownloadDicomSeries = <TData = Blob>(
    variables: DownloadDicomSeriesVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, DownloadDicomSeriesError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, DownloadDicomSeriesError, TData>({
        queryKey: queryKeyFn({
            path: '/api/studies/{studyId}/series/{seriesId}.zip',
            operationId: 'downloadDicomSeries',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchDownloadDicomSeries(
                { ...fetcherOptions, ...variables },
                signal
            ),
        ...options,
        ...queryOptions,
    });
};

export type DownloadDicomImagePathParams = {
    studyId: string;
    seriesId: string;
    instanceId: string;
};

export type DownloadDicomImageError = Fetcher.ErrorWrapper<undefined>;

export type DownloadDicomImageVariables = {
    pathParams: DownloadDicomImagePathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDownloadDicomImage = (
    variables: DownloadDicomImageVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        DownloadDicomImageError,
        undefined,
        {},
        {},
        DownloadDicomImagePathParams
    >({
        url: '/api/studies/{studyId}/series/{seriesId}/slices/{instanceId}.dcm',
        method: 'get',
        ...variables,
        signal,
    });

export const useDownloadDicomImage = <TData = Blob>(
    variables: DownloadDicomImageVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, DownloadDicomImageError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, DownloadDicomImageError, TData>({
        queryKey: queryKeyFn({
            path: '/api/studies/{studyId}/series/{seriesId}/slices/{instanceId}.dcm',
            operationId: 'downloadDicomImage',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchDownloadDicomImage(
                { ...fetcherOptions, ...variables },
                signal
            ),
        ...options,
        ...queryOptions,
    });
};

export type DownloadDicomImageBySOPPathParams = {
    patientID: string;
    modality: Schemas.Modality;
    sopInstanceUID: string;
};

export type DownloadDicomImageBySOPError = Fetcher.ErrorWrapper<undefined>;

export type DownloadDicomImageBySOPVariables = {
    pathParams: DownloadDicomImageBySOPPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDownloadDicomImageBySOP = (
    variables: DownloadDicomImageBySOPVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        DownloadDicomImageBySOPError,
        undefined,
        {},
        {},
        DownloadDicomImageBySOPPathParams
    >({
        url: '/api/studies/{patientID}/{modality}/{sopInstanceUID}.dcm',
        method: 'get',
        ...variables,
        signal,
    });

export const useDownloadDicomImageBySOP = <TData = Blob>(
    variables: DownloadDicomImageBySOPVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, DownloadDicomImageBySOPError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, DownloadDicomImageBySOPError, TData>({
        queryKey: queryKeyFn({
            path: '/api/studies/{patientID}/{modality}/{sopInstanceUID}.dcm',
            operationId: 'downloadDicomImageBySOP',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchDownloadDicomImageBySOP(
                { ...fetcherOptions, ...variables },
                signal
            ),
        ...options,
        ...queryOptions,
    });
};

export type GetPatientsError = Fetcher.ErrorWrapper<undefined>;

export type GetPatientsResponse = Schemas.PatientPresentation[];

export type GetPatientsVariables = ConnectorContext['fetcherOptions'];

export const fetchGetPatients = (
    variables: GetPatientsVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        GetPatientsResponse,
        GetPatientsError,
        undefined,
        {},
        {},
        {}
    >({ url: '/api/patients', method: 'get', ...variables, signal });

export const useGetPatients = <TData = GetPatientsResponse>(
    variables: GetPatientsVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            GetPatientsResponse,
            GetPatientsError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<GetPatientsResponse, GetPatientsError, TData>({
        queryKey: queryKeyFn({
            path: '/api/patients',
            operationId: 'getPatients',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetPatients({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type CreatePatientError = Fetcher.ErrorWrapper<undefined>;

export type CreatePatientRequestBody = Schemas.PatientCreationPayload;

export type CreatePatientVariables = {
    body?: CreatePatientRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchCreatePatient = (
    variables: CreatePatientVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientPresentation,
        CreatePatientError,
        CreatePatientRequestBody,
        {},
        {},
        {}
    >({ url: '/api/patients', method: 'post', ...variables, signal });

export const useCreatePatient = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.PatientPresentation,
            CreatePatientError,
            CreatePatientVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.PatientPresentation,
        CreatePatientError,
        CreatePatientVariables
    >({
        mutationFn: (variables: CreatePatientVariables) =>
            fetchCreatePatient({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetPatientPathParams = {
    patientId: string;
};

export type GetPatientError = Fetcher.ErrorWrapper<undefined>;

export type GetPatientVariables = {
    pathParams: GetPatientPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetPatient = (
    variables: GetPatientVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientPresentation,
        GetPatientError,
        undefined,
        {},
        {},
        GetPatientPathParams
    >({
        url: '/api/patients/{patientId}',
        method: 'get',
        ...variables,
        signal,
    });

export const useGetPatient = <TData = Schemas.PatientPresentation>(
    variables: GetPatientVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            Schemas.PatientPresentation,
            GetPatientError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<
        Schemas.PatientPresentation,
        GetPatientError,
        TData
    >({
        queryKey: queryKeyFn({
            path: '/api/patients/{patientId}',
            operationId: 'getPatient',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetPatient({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type SyncPatientPathParams = {
    patientID: string;
};

export type SyncPatientError = Fetcher.ErrorWrapper<undefined>;

export type SyncPatientRequestBody = Schemas.SeriesSyncPayload[];

export type SyncPatientVariables = {
    body?: SyncPatientRequestBody;
    pathParams: SyncPatientPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchSyncPatient = (
    variables: SyncPatientVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientPresentation,
        SyncPatientError,
        SyncPatientRequestBody,
        {},
        {},
        SyncPatientPathParams
    >({
        url: '/api/patients/{patientID}',
        method: 'put',
        ...variables,
        signal,
    });

export const useSyncPatient = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.PatientPresentation,
            SyncPatientError,
            SyncPatientVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.PatientPresentation,
        SyncPatientError,
        SyncPatientVariables
    >({
        mutationFn: (variables: SyncPatientVariables) =>
            fetchSyncPatient({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type DeletePatientImageReferencePathParams = {
    patientID: string;
    studyID: string;
    seriesID: string;
};

export type DeletePatientImageReferenceError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: Schemas.PatientPresentation;
      }
    | {
          status: 500;
          payload: Schemas.PatientPresentation;
      }
>;

export type DeletePatientImageReferenceVariables = {
    pathParams: DeletePatientImageReferencePathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDeletePatientImageReference = (
    variables: DeletePatientImageReferenceVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientPresentation,
        DeletePatientImageReferenceError,
        undefined,
        {},
        {},
        DeletePatientImageReferencePathParams
    >({
        url: '/api/patients/{patientID}/images/{studyID}/{seriesID}',
        method: 'delete',
        ...variables,
        signal,
    });

export const useDeletePatientImageReference = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.PatientPresentation,
            DeletePatientImageReferenceError,
            DeletePatientImageReferenceVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.PatientPresentation,
        DeletePatientImageReferenceError,
        DeletePatientImageReferenceVariables
    >({
        mutationFn: (variables: DeletePatientImageReferenceVariables) =>
            fetchDeletePatientImageReference({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type DeletePatientRTStructReferencePathParams = {
    patientID: string;
    studyID: string;
    seriesID: string;
    sliceID: string;
};

export type DeletePatientRTStructReferenceError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: Schemas.PatientPresentation;
      }
    | {
          status: 500;
          payload: Schemas.PatientPresentation;
      }
>;

export type DeletePatientRTStructReferenceVariables = {
    pathParams: DeletePatientRTStructReferencePathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDeletePatientRTStructReference = (
    variables: DeletePatientRTStructReferenceVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientPresentation,
        DeletePatientRTStructReferenceError,
        undefined,
        {},
        {},
        DeletePatientRTStructReferencePathParams
    >({
        url: '/api/patients/{patientID}/structuresets/{studyID}/{seriesID}/{sliceID}',
        method: 'delete',
        ...variables,
        signal,
    });

export const useDeletePatientRTStructReference = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.PatientPresentation,
            DeletePatientRTStructReferenceError,
            DeletePatientRTStructReferenceVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.PatientPresentation,
        DeletePatientRTStructReferenceError,
        DeletePatientRTStructReferenceVariables
    >({
        mutationFn: (variables: DeletePatientRTStructReferenceVariables) =>
            fetchDeletePatientRTStructReference({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type DeletePatientDoseReferencePathParams = {
    patientID: string;
    studyID: string;
    seriesID: string;
    sliceID: string;
};

export type DeletePatientDoseReferenceError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: Schemas.PatientPresentation;
      }
    | {
          status: 500;
          payload: Schemas.PatientPresentation;
      }
>;

export type DeletePatientDoseReferenceVariables = {
    pathParams: DeletePatientDoseReferencePathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDeletePatientDoseReference = (
    variables: DeletePatientDoseReferenceVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientPresentation,
        DeletePatientDoseReferenceError,
        undefined,
        {},
        {},
        DeletePatientDoseReferencePathParams
    >({
        url: '/api/patients/{patientID}/doses/{studyID}/{seriesID}/{sliceID}',
        method: 'delete',
        ...variables,
        signal,
    });

export const useDeletePatientDoseReference = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.PatientPresentation,
            DeletePatientDoseReferenceError,
            DeletePatientDoseReferenceVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.PatientPresentation,
        DeletePatientDoseReferenceError,
        DeletePatientDoseReferenceVariables
    >({
        mutationFn: (variables: DeletePatientDoseReferenceVariables) =>
            fetchDeletePatientDoseReference({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type DeletePatientRegReferencePathParams = {
    patientID: string;
    studyID: string;
    seriesID: string;
    sliceID: string;
};

export type DeletePatientRegReferenceError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: Schemas.PatientPresentation;
      }
    | {
          status: 500;
          payload: Schemas.PatientPresentation;
      }
>;

export type DeletePatientRegReferenceVariables = {
    pathParams: DeletePatientRegReferencePathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDeletePatientRegReference = (
    variables: DeletePatientRegReferenceVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientPresentation,
        DeletePatientRegReferenceError,
        undefined,
        {},
        {},
        DeletePatientRegReferencePathParams
    >({
        url: '/api/patients/{patientID}/regs/{studyID}/{seriesID}/{sliceID}',
        method: 'delete',
        ...variables,
        signal,
    });

export const useDeletePatientRegReference = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.PatientPresentation,
            DeletePatientRegReferenceError,
            DeletePatientRegReferenceVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.PatientPresentation,
        DeletePatientRegReferenceError,
        DeletePatientRegReferenceVariables
    >({
        mutationFn: (variables: DeletePatientRegReferenceVariables) =>
            fetchDeletePatientRegReference({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type DeletePatientPlanReferencePathParams = {
    patientID: string;
    studyID: string;
    seriesID: string;
    sliceID: string;
};

export type DeletePatientPlanReferenceError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: Schemas.PatientPresentation;
      }
    | {
          status: 500;
          payload: Schemas.PatientPresentation;
      }
>;

export type DeletePatientPlanReferenceVariables = {
    pathParams: DeletePatientPlanReferencePathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDeletePatientPlanReference = (
    variables: DeletePatientPlanReferenceVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.PatientPresentation,
        DeletePatientPlanReferenceError,
        undefined,
        {},
        {},
        DeletePatientPlanReferencePathParams
    >({
        url: '/api/patients/{patientID}/plans/{studyID}/{seriesID}/{sliceID}',
        method: 'delete',
        ...variables,
        signal,
    });

export const useDeletePatientPlanReference = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.PatientPresentation,
            DeletePatientPlanReferenceError,
            DeletePatientPlanReferenceVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.PatientPresentation,
        DeletePatientPlanReferenceError,
        DeletePatientPlanReferenceVariables
    >({
        mutationFn: (variables: DeletePatientPlanReferenceVariables) =>
            fetchDeletePatientPlanReference({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type SaveDICOMForPatientPathParams = {
    patientID: string;
};

export type SaveDICOMForPatientError = Fetcher.ErrorWrapper<undefined>;

export type SaveDICOMForPatientRequestBody = {
    files: Blob[];
};

export type SaveDICOMForPatientVariables = {
    body: SaveDICOMForPatientRequestBody;
    pathParams: SaveDICOMForPatientPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchSaveDICOMForPatient = (
    variables: SaveDICOMForPatientVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        SaveDICOMForPatientError,
        SaveDICOMForPatientRequestBody,
        {},
        {},
        SaveDICOMForPatientPathParams
    >({
        url: '/api/patients/{patientID}/datasets',
        method: 'post',
        ...variables,
        signal,
    });

export const useSaveDICOMForPatient = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Blob,
            SaveDICOMForPatientError,
            SaveDICOMForPatientVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Blob,
        SaveDICOMForPatientError,
        SaveDICOMForPatientVariables
    >({
        mutationFn: (variables: SaveDICOMForPatientVariables) =>
            fetchSaveDICOMForPatient({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type SaveSliceForImageForPatientPathParams = {
    patientID: string;
    imageID: string;
};

export type SaveSliceForImageForPatientError = Fetcher.ErrorWrapper<undefined>;

export type SaveSliceForImageForPatientRequestBody = {
    files: Blob[];
};

export type SaveSliceForImageForPatientVariables = {
    body: SaveSliceForImageForPatientRequestBody;
    pathParams: SaveSliceForImageForPatientPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchSaveSliceForImageForPatient = (
    variables: SaveSliceForImageForPatientVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        SaveSliceForImageForPatientError,
        SaveSliceForImageForPatientRequestBody,
        {},
        {},
        SaveSliceForImageForPatientPathParams
    >({
        url: '/api/patients/{patientID}/images/{imageID}/slices',
        method: 'post',
        ...variables,
        signal,
    });

export const useSaveSliceForImageForPatient = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Blob,
            SaveSliceForImageForPatientError,
            SaveSliceForImageForPatientVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Blob,
        SaveSliceForImageForPatientError,
        SaveSliceForImageForPatientVariables
    >({
        mutationFn: (variables: SaveSliceForImageForPatientVariables) =>
            fetchSaveSliceForImageForPatient({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type UploadImageToTargetPathParams = {
    patientID: string;
    /**
     * @format uuid
     */
    imageID: string;
};

export type UploadImageToTargetQueryParams = {
    target: string;
};

export type UploadImageToTargetError = Fetcher.ErrorWrapper<undefined>;

export type UploadImageToTargetResponse = {
    [key: string]: boolean;
};

export type UploadImageToTargetVariables = {
    pathParams: UploadImageToTargetPathParams;
    queryParams: UploadImageToTargetQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchUploadImageToTarget = (
    variables: UploadImageToTargetVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        UploadImageToTargetResponse,
        UploadImageToTargetError,
        undefined,
        {},
        UploadImageToTargetQueryParams,
        UploadImageToTargetPathParams
    >({
        url: '/api/patients/{patientID}/images/{imageID}/export',
        method: 'post',
        ...variables,
        signal,
    });

export const useUploadImageToTarget = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            UploadImageToTargetResponse,
            UploadImageToTargetError,
            UploadImageToTargetVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        UploadImageToTargetResponse,
        UploadImageToTargetError,
        UploadImageToTargetVariables
    >({
        mutationFn: (variables: UploadImageToTargetVariables) =>
            fetchUploadImageToTarget({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type UploadRTStructToTargetPathParams = {
    patientID: string;
    /**
     * @format uuid
     */
    structureID: string;
};

export type UploadRTStructToTargetQueryParams = {
    target: string;
};

export type UploadRTStructToTargetError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: {
              [key: string]: boolean;
          };
      }
    | {
          status: 500;
          payload: {
              [key: string]: boolean;
          };
      }
>;

export type UploadRTStructToTargetResponse = {
    [key: string]: boolean;
};

export type UploadRTStructToTargetVariables = {
    pathParams: UploadRTStructToTargetPathParams;
    queryParams: UploadRTStructToTargetQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchUploadRTStructToTarget = (
    variables: UploadRTStructToTargetVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        UploadRTStructToTargetResponse,
        UploadRTStructToTargetError,
        undefined,
        {},
        UploadRTStructToTargetQueryParams,
        UploadRTStructToTargetPathParams
    >({
        url: '/api/patients/{patientID}/structuresets/{structureID}/export',
        method: 'post',
        ...variables,
        signal,
    });

export const useUploadRTStructToTarget = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            UploadRTStructToTargetResponse,
            UploadRTStructToTargetError,
            UploadRTStructToTargetVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        UploadRTStructToTargetResponse,
        UploadRTStructToTargetError,
        UploadRTStructToTargetVariables
    >({
        mutationFn: (variables: UploadRTStructToTargetVariables) =>
            fetchUploadRTStructToTarget({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type UploadDoseToTargetPathParams = {
    patientID: string;
    /**
     * @format uuid
     */
    doseID: string;
};

export type UploadDoseToTargetQueryParams = {
    target: string;
};

export type UploadDoseToTargetError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: {
              [key: string]: boolean;
          };
      }
    | {
          status: 500;
          payload: {
              [key: string]: boolean;
          };
      }
>;

export type UploadDoseToTargetResponse = {
    [key: string]: boolean;
};

export type UploadDoseToTargetVariables = {
    pathParams: UploadDoseToTargetPathParams;
    queryParams: UploadDoseToTargetQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchUploadDoseToTarget = (
    variables: UploadDoseToTargetVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        UploadDoseToTargetResponse,
        UploadDoseToTargetError,
        undefined,
        {},
        UploadDoseToTargetQueryParams,
        UploadDoseToTargetPathParams
    >({
        url: '/api/patients/{patientID}/doses/{doseID}/export',
        method: 'post',
        ...variables,
        signal,
    });

export const useUploadDoseToTarget = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            UploadDoseToTargetResponse,
            UploadDoseToTargetError,
            UploadDoseToTargetVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        UploadDoseToTargetResponse,
        UploadDoseToTargetError,
        UploadDoseToTargetVariables
    >({
        mutationFn: (variables: UploadDoseToTargetVariables) =>
            fetchUploadDoseToTarget({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type UploadRegToTargetPathParams = {
    patientID: string;
    /**
     * @format uuid
     */
    regID: string;
};

export type UploadRegToTargetQueryParams = {
    target: string;
};

export type UploadRegToTargetError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: {
              [key: string]: boolean;
          };
      }
    | {
          status: 500;
          payload: {
              [key: string]: boolean;
          };
      }
>;

export type UploadRegToTargetResponse = {
    [key: string]: boolean;
};

export type UploadRegToTargetVariables = {
    pathParams: UploadRegToTargetPathParams;
    queryParams: UploadRegToTargetQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchUploadRegToTarget = (
    variables: UploadRegToTargetVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        UploadRegToTargetResponse,
        UploadRegToTargetError,
        undefined,
        {},
        UploadRegToTargetQueryParams,
        UploadRegToTargetPathParams
    >({
        url: '/api/patients/{patientID}/regs/{regID}/export',
        method: 'post',
        ...variables,
        signal,
    });

export const useUploadRegToTarget = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            UploadRegToTargetResponse,
            UploadRegToTargetError,
            UploadRegToTargetVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        UploadRegToTargetResponse,
        UploadRegToTargetError,
        UploadRegToTargetVariables
    >({
        mutationFn: (variables: UploadRegToTargetVariables) =>
            fetchUploadRegToTarget({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type UploadPlanToTargetPathParams = {
    patientID: string;
    /**
     * @format uuid
     */
    planID: string;
};

export type UploadPlanToTargetQueryParams = {
    target: string;
};

export type UploadPlanToTargetError = Fetcher.ErrorWrapper<
    | {
          status: 404;
          payload: {
              [key: string]: boolean;
          };
      }
    | {
          status: 500;
          payload: {
              [key: string]: boolean;
          };
      }
>;

export type UploadPlanToTargetResponse = {
    [key: string]: boolean;
};

export type UploadPlanToTargetVariables = {
    pathParams: UploadPlanToTargetPathParams;
    queryParams: UploadPlanToTargetQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchUploadPlanToTarget = (
    variables: UploadPlanToTargetVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        UploadPlanToTargetResponse,
        UploadPlanToTargetError,
        undefined,
        {},
        UploadPlanToTargetQueryParams,
        UploadPlanToTargetPathParams
    >({
        url: '/api/patients/{patientID}/plans/{planID}/export',
        method: 'post',
        ...variables,
        signal,
    });

export const useUploadPlanToTarget = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            UploadPlanToTargetResponse,
            UploadPlanToTargetError,
            UploadPlanToTargetVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        UploadPlanToTargetResponse,
        UploadPlanToTargetError,
        UploadPlanToTargetVariables
    >({
        mutationFn: (variables: UploadPlanToTargetVariables) =>
            fetchUploadPlanToTarget({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type EchoError = Fetcher.ErrorWrapper<undefined>;

export type EchoVariables = ConnectorContext['fetcherOptions'];

export const fetchEcho = (variables: EchoVariables, signal?: AbortSignal) =>
    connectorFetch<string, EchoError, undefined, {}, {}, {}>({
        url: '/api/dicom/echo',
        method: 'get',
        ...variables,
        signal,
    });

export const useEcho = <TData = string>(
    variables: EchoVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<string, EchoError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<string, EchoError, TData>({
        queryKey: queryKeyFn({
            path: '/api/dicom/echo',
            operationId: 'echo',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchEcho({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type UploadDICOMPathParams = {
    target: string;
};

export type UploadDICOMError = Fetcher.ErrorWrapper<undefined>;

export type UploadDICOMResponse = {
    [key: string]: boolean;
};

export type UploadDICOMRequestBody = {
    files: Blob[];
};

export type UploadDICOMVariables = {
    body: UploadDICOMRequestBody;
    pathParams: UploadDICOMPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchUploadDICOM = (
    variables: UploadDICOMVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        UploadDICOMResponse,
        UploadDICOMError,
        UploadDICOMRequestBody,
        {},
        {},
        UploadDICOMPathParams
    >({ url: '/api/dicom/{target}', method: 'post', ...variables, signal });

export const useUploadDICOM = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            UploadDICOMResponse,
            UploadDICOMError,
            UploadDICOMVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        UploadDICOMResponse,
        UploadDICOMError,
        UploadDICOMVariables
    >({
        mutationFn: (variables: UploadDICOMVariables) =>
            fetchUploadDICOM({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type ListTargetsError = Fetcher.ErrorWrapper<undefined>;

export type ListTargetsResponse = {
    [key: string]: Schemas.FlowDirection;
};

export type ListTargetsVariables = ConnectorContext['fetcherOptions'];

export const fetchListTargets = (
    variables: ListTargetsVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        ListTargetsResponse,
        ListTargetsError,
        undefined,
        {},
        {},
        {}
    >({ url: '/api/dicom/targets', method: 'get', ...variables, signal });

export const useListTargets = <TData = ListTargetsResponse>(
    variables: ListTargetsVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            ListTargetsResponse,
            ListTargetsError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<ListTargetsResponse, ListTargetsError, TData>({
        queryKey: queryKeyFn({
            path: '/api/dicom/targets',
            operationId: 'listTargets',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchListTargets({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type SaveSessionError = Fetcher.ErrorWrapper<undefined>;

export type SaveSessionRequestBody = Schemas.SessionInput;

export type SaveSessionVariables = {
    body?: SaveSessionRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchSaveSession = (
    variables: SaveSessionVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        string,
        SaveSessionError,
        SaveSessionRequestBody,
        {},
        {},
        {}
    >({ url: '/api/session', method: 'post', ...variables, signal });

export const useSaveSession = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            string,
            SaveSessionError,
            SaveSessionVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        string,
        SaveSessionError,
        SaveSessionVariables
    >({
        mutationFn: (variables: SaveSessionVariables) =>
            fetchSaveSession({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetSessionPathParams = {
    /**
     * @format uuid
     */
    sessionID: string;
};

export type GetSessionError = Fetcher.ErrorWrapper<undefined>;

export type GetSessionVariables = {
    pathParams: GetSessionPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetSession = (
    variables: GetSessionVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.SessionPresentation,
        GetSessionError,
        undefined,
        {},
        {},
        GetSessionPathParams
    >({ url: '/api/session/{sessionID}', method: 'get', ...variables, signal });

export const useGetSession = <TData = Schemas.SessionPresentation>(
    variables: GetSessionVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            Schemas.SessionPresentation,
            GetSessionError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<
        Schemas.SessionPresentation,
        GetSessionError,
        TData
    >({
        queryKey: queryKeyFn({
            path: '/api/session/{sessionID}',
            operationId: 'getSession',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetSession({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type RequestImageDownloadError = Fetcher.ErrorWrapper<undefined>;

export type RequestImageDownloadRequestBody = Schemas.ImageDownloadRequest;

export type RequestImageDownloadVariables = {
    body?: RequestImageDownloadRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchRequestImageDownload = (
    variables: RequestImageDownloadVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        RequestImageDownloadError,
        RequestImageDownloadRequestBody,
        {},
        {},
        {}
    >({
        url: '/api/images/request-download',
        method: 'post',
        ...variables,
        signal,
    });

export const useRequestImageDownload = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            RequestImageDownloadError,
            RequestImageDownloadVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        RequestImageDownloadError,
        RequestImageDownloadVariables
    >({
        mutationFn: (variables: RequestImageDownloadVariables) =>
            fetchRequestImageDownload({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type SaveWorkflowPathParams = {
    patientId: string;
};

export type SaveWorkflowError = Fetcher.ErrorWrapper<undefined>;

export type SaveWorkflowRequestBody = Schemas.WorkflowInput;

export type SaveWorkflowVariables = {
    body?: SaveWorkflowRequestBody;
    pathParams: SaveWorkflowPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchSaveWorkflow = (
    variables: SaveWorkflowVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.WorkflowPresentation,
        SaveWorkflowError,
        SaveWorkflowRequestBody,
        {},
        {},
        SaveWorkflowPathParams
    >({
        url: '/api/patients/{patientId}/workflows',
        method: 'post',
        ...variables,
        signal,
    });

export const useSaveWorkflow = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.WorkflowPresentation,
            SaveWorkflowError,
            SaveWorkflowVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.WorkflowPresentation,
        SaveWorkflowError,
        SaveWorkflowVariables
    >({
        mutationFn: (variables: SaveWorkflowVariables) =>
            fetchSaveWorkflow({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetWorkflowPathParams = {
    patientId: string;
    /**
     * @format uuid
     */
    workflowID: string;
};

export type GetWorkflowError = Fetcher.ErrorWrapper<undefined>;

export type GetWorkflowVariables = {
    pathParams: GetWorkflowPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetWorkflow = (
    variables: GetWorkflowVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.WorkflowPresentation,
        GetWorkflowError,
        undefined,
        {},
        {},
        GetWorkflowPathParams
    >({
        url: '/api/patients/{patientId}/workflows/{workflowID}',
        method: 'get',
        ...variables,
        signal,
    });

export const useGetWorkflow = <TData = Schemas.WorkflowPresentation>(
    variables: GetWorkflowVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            Schemas.WorkflowPresentation,
            GetWorkflowError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<
        Schemas.WorkflowPresentation,
        GetWorkflowError,
        TData
    >({
        queryKey: queryKeyFn({
            path: '/api/patients/{patientId}/workflows/{workflowID}',
            operationId: 'getWorkflow',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetWorkflow({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type DeleteWorkflowPathParams = {
    patientId: string;
    /**
     * @format uuid
     */
    workflowID: string;
};

export type DeleteWorkflowError = Fetcher.ErrorWrapper<undefined>;

export type DeleteWorkflowVariables = {
    pathParams: DeleteWorkflowPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchDeleteWorkflow = (
    variables: DeleteWorkflowVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        DeleteWorkflowError,
        undefined,
        {},
        {},
        DeleteWorkflowPathParams
    >({
        url: '/api/patients/{patientId}/workflows/{workflowID}',
        method: 'delete',
        ...variables,
        signal,
    });

export const useDeleteWorkflow = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            DeleteWorkflowError,
            DeleteWorkflowVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        DeleteWorkflowError,
        DeleteWorkflowVariables
    >({
        mutationFn: (variables: DeleteWorkflowVariables) =>
            fetchDeleteWorkflow({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type DataTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type DataTaskProcessingRequestBody = Schemas.DataProcessingRequest;

export type DataTaskProcessingVariables = {
    body?: DataTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchDataTaskProcessing = (
    variables: DataTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        DataTaskProcessingError,
        DataTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/data', method: 'post', ...variables, signal });

export const useDataTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskPresentation,
            DataTaskProcessingError,
            DataTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskPresentation,
        DataTaskProcessingError,
        DataTaskProcessingVariables
    >({
        mutationFn: (variables: DataTaskProcessingVariables) =>
            fetchDataTaskProcessing({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type SegmentationTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type SegmentationTaskProcessingRequestBody =
    Schemas.SegmentationProcessingRequest;

export type SegmentationTaskProcessingVariables = {
    body?: SegmentationTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchSegmentationTaskProcessing = (
    variables: SegmentationTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        SegmentationTaskProcessingError,
        SegmentationTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/segmentation', method: 'post', ...variables, signal });

export const useSegmentationTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskPresentation,
            SegmentationTaskProcessingError,
            SegmentationTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskPresentation,
        SegmentationTaskProcessingError,
        SegmentationTaskProcessingVariables
    >({
        mutationFn: (variables: SegmentationTaskProcessingVariables) =>
            fetchSegmentationTaskProcessing({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type DoseTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type DoseTaskProcessingRequestBody = Schemas.DoseProcessingRequest;

export type DoseTaskProcessingVariables = {
    body?: DoseTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchDoseTaskProcessing = (
    variables: DoseTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        DoseTaskProcessingError,
        DoseTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/dose', method: 'post', ...variables, signal });

export const useDoseTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskPresentation,
            DoseTaskProcessingError,
            DoseTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskPresentation,
        DoseTaskProcessingError,
        DoseTaskProcessingVariables
    >({
        mutationFn: (variables: DoseTaskProcessingVariables) =>
            fetchDoseTaskProcessing({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type DoseCalcTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type DoseCalcTaskProcessingRequestBody =
    Schemas.DoseCalcProcessingRequest;

export type DoseCalcTaskProcessingVariables = {
    body?: DoseCalcTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchDoseCalcTaskProcessing = (
    variables: DoseCalcTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        DoseCalcTaskProcessingError,
        DoseCalcTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/dosecalc', method: 'post', ...variables, signal });

export const useDoseCalcTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskPresentation,
            DoseCalcTaskProcessingError,
            DoseCalcTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskPresentation,
        DoseCalcTaskProcessingError,
        DoseCalcTaskProcessingVariables
    >({
        mutationFn: (variables: DoseCalcTaskProcessingVariables) =>
            fetchDoseCalcTaskProcessing({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type ImageProcessingTaskProcessingError =
    Fetcher.ErrorWrapper<undefined>;

export type ImageProcessingTaskProcessingRequestBody =
    Schemas.ImageProcessingRequest;

export type ImageProcessingTaskProcessingVariables = {
    body?: ImageProcessingTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchImageProcessingTaskProcessing = (
    variables: ImageProcessingTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        ImageProcessingTaskProcessingError,
        ImageProcessingTaskProcessingRequestBody,
        {},
        {},
        {}
    >({
        url: '/api/tasks/imageprocessing',
        method: 'post',
        ...variables,
        signal,
    });

export const useImageProcessingTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskPresentation,
            ImageProcessingTaskProcessingError,
            ImageProcessingTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskPresentation,
        ImageProcessingTaskProcessingError,
        ImageProcessingTaskProcessingVariables
    >({
        mutationFn: (variables: ImageProcessingTaskProcessingVariables) =>
            fetchImageProcessingTaskProcessing({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type RegistrationTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type RegistrationTaskProcessingRequestBody =
    Schemas.RegProcessingRequest;

export type RegistrationTaskProcessingVariables = {
    body?: RegistrationTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchRegistrationTaskProcessing = (
    variables: RegistrationTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        RegistrationTaskProcessingError,
        RegistrationTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/registration', method: 'post', ...variables, signal });

export const useRegistrationTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskPresentation,
            RegistrationTaskProcessingError,
            RegistrationTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskPresentation,
        RegistrationTaskProcessingError,
        RegistrationTaskProcessingVariables
    >({
        mutationFn: (variables: RegistrationTaskProcessingVariables) =>
            fetchRegistrationTaskProcessing({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type CompositeCbctTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type CompositeCbctTaskProcessingRequestBody =
    Schemas.CompositeCbctProcessingRequest;

export type CompositeCbctTaskProcessingVariables = {
    body?: CompositeCbctTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchCompositeCbctTaskProcessing = (
    variables: CompositeCbctTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        CompositeCbctTaskProcessingError,
        CompositeCbctTaskProcessingRequestBody,
        {},
        {},
        {}
    >({
        url: '/api/tasks/composite-cbct',
        method: 'post',
        ...variables,
        signal,
    });

export const useCompositeCbctTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskPresentation,
            CompositeCbctTaskProcessingError,
            CompositeCbctTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskPresentation,
        CompositeCbctTaskProcessingError,
        CompositeCbctTaskProcessingVariables
    >({
        mutationFn: (variables: CompositeCbctTaskProcessingVariables) =>
            fetchCompositeCbctTaskProcessing({
                ...fetcherOptions,
                ...variables,
            }),
        ...options,
    });
};

export type TranslationTaskProcessingError = Fetcher.ErrorWrapper<undefined>;

export type TranslationTaskProcessingRequestBody =
    Schemas.TranslationProcessingRequest;

export type TranslationTaskProcessingVariables = {
    body?: TranslationTaskProcessingRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchTranslationTaskProcessing = (
    variables: TranslationTaskProcessingVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        TranslationTaskProcessingError,
        TranslationTaskProcessingRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/translation', method: 'post', ...variables, signal });

export const useTranslationTaskProcessing = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Schemas.TaskPresentation,
            TranslationTaskProcessingError,
            TranslationTaskProcessingVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Schemas.TaskPresentation,
        TranslationTaskProcessingError,
        TranslationTaskProcessingVariables
    >({
        mutationFn: (variables: TranslationTaskProcessingVariables) =>
            fetchTranslationTaskProcessing({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetTasksListError = Fetcher.ErrorWrapper<undefined>;

export type GetTasksListVariables = ConnectorContext['fetcherOptions'];

export const fetchGetTasksList = (
    variables: GetTasksListVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        GetTasksListError,
        undefined,
        {},
        {},
        {}
    >({ url: '/api/tasks', method: 'get', ...variables, signal });

export const useGetTasksList = <TData = Schemas.TaskPresentation>(
    variables: GetTasksListVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            Schemas.TaskPresentation,
            GetTasksListError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<
        Schemas.TaskPresentation,
        GetTasksListError,
        TData
    >({
        queryKey: queryKeyFn({
            path: '/api/tasks',
            operationId: 'getTasksList',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetTasksList({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type GetTaskStatusPathParams = {
    taskID: string;
};

export type GetTaskStatusError = Fetcher.ErrorWrapper<undefined>;

export type GetTaskStatusVariables = {
    pathParams: GetTaskStatusPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetTaskStatus = (
    variables: GetTaskStatusVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Schemas.TaskPresentation,
        GetTaskStatusError,
        undefined,
        {},
        {},
        GetTaskStatusPathParams
    >({ url: '/api/tasks/{taskID}', method: 'get', ...variables, signal });

export const useGetTaskStatus = <TData = Schemas.TaskPresentation>(
    variables: GetTaskStatusVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            Schemas.TaskPresentation,
            GetTaskStatusError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<
        Schemas.TaskPresentation,
        GetTaskStatusError,
        TData
    >({
        queryKey: queryKeyFn({
            path: '/api/tasks/{taskID}',
            operationId: 'getTaskStatus',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetTaskStatus({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type CancelTaskPathParams = {
    taskID: string;
};

export type CancelTaskError = Fetcher.ErrorWrapper<undefined>;

export type CancelTaskVariables = {
    pathParams: CancelTaskPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchCancelTask = (
    variables: CancelTaskVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        CancelTaskError,
        undefined,
        {},
        {},
        CancelTaskPathParams
    >({ url: '/api/tasks/{taskID}', method: 'delete', ...variables, signal });

export const useCancelTask = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            CancelTaskError,
            CancelTaskVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        CancelTaskError,
        CancelTaskVariables
    >({
        mutationFn: (variables: CancelTaskVariables) =>
            fetchCancelTask({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetTaskResultPathParams = {
    taskID: string;
    resultPath: string;
};

export type GetTaskResultQueryParams = {
    /**
     * @default false
     */
    skipReidentification?: boolean;
};

export type GetTaskResultError = Fetcher.ErrorWrapper<undefined>;

export type GetTaskResultVariables = {
    pathParams: GetTaskResultPathParams;
    queryParams?: GetTaskResultQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetTaskResult = (
    variables: GetTaskResultVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        GetTaskResultError,
        undefined,
        {},
        GetTaskResultQueryParams,
        GetTaskResultPathParams
    >({
        url: '/api/tasks/{taskID}/results/{resultPath}',
        method: 'get',
        ...variables,
        signal,
    });

export const useGetTaskResult = <TData = Blob>(
    variables: GetTaskResultVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, GetTaskResultError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, GetTaskResultError, TData>({
        queryKey: queryKeyFn({
            path: '/api/tasks/{taskID}/results/{resultPath}',
            operationId: 'getTaskResult',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetTaskResult({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type GetTaskErrorPathParams = {
    taskID: string;
    errorPath: string;
};

export type GetTaskErrorError = Fetcher.ErrorWrapper<undefined>;

export type GetTaskErrorVariables = {
    pathParams: GetTaskErrorPathParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetTaskError = (
    variables: GetTaskErrorVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        GetTaskErrorError,
        undefined,
        {},
        {},
        GetTaskErrorPathParams
    >({
        url: '/api/tasks/{taskID}/errors/{errorPath}',
        method: 'get',
        ...variables,
        signal,
    });

export const useGetTaskError = <TData = Blob>(
    variables: GetTaskErrorVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<Blob, GetTaskErrorError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<Blob, GetTaskErrorError, TData>({
        queryKey: queryKeyFn({
            path: '/api/tasks/{taskID}/errors/{errorPath}',
            operationId: 'getTaskError',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetTaskError({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type SaveDICOMToCacheError = Fetcher.ErrorWrapper<undefined>;

export type SaveDICOMToCacheRequestBody = {
    files: Blob[];
};

export type SaveDICOMToCacheVariables = {
    body: SaveDICOMToCacheRequestBody;
} & ConnectorContext['fetcherOptions'];

export const fetchSaveDICOMToCache = (
    variables: SaveDICOMToCacheVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        Blob,
        SaveDICOMToCacheError,
        SaveDICOMToCacheRequestBody,
        {},
        {},
        {}
    >({ url: '/api/tasks/cache', method: 'put', ...variables, signal });

export const useSaveDICOMToCache = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            Blob,
            SaveDICOMToCacheError,
            SaveDICOMToCacheVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        Blob,
        SaveDICOMToCacheError,
        SaveDICOMToCacheVariables
    >({
        mutationFn: (variables: SaveDICOMToCacheVariables) =>
            fetchSaveDICOMToCache({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type GetDicomPatientsQueryParams = {
    patientIDTerm: string;
    targetName?: string;
};

export type GetDicomPatientsError = Fetcher.ErrorWrapper<undefined>;

export type GetDicomPatientsResponse = Schemas.DICOMPatientPresentation[];

export type GetDicomPatientsVariables = {
    queryParams: GetDicomPatientsQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetDicomPatients = (
    variables: GetDicomPatientsVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        GetDicomPatientsResponse,
        GetDicomPatientsError,
        undefined,
        {},
        GetDicomPatientsQueryParams,
        {}
    >({ url: '/api/dicom/patients', method: 'get', ...variables, signal });

export const useGetDicomPatients = <TData = GetDicomPatientsResponse>(
    variables: GetDicomPatientsVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            GetDicomPatientsResponse,
            GetDicomPatientsError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<
        GetDicomPatientsResponse,
        GetDicomPatientsError,
        TData
    >({
        queryKey: queryKeyFn({
            path: '/api/dicom/patients',
            operationId: 'getDicomPatients',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetDicomPatients({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type GetDicomPatientStudiesPathParams = {
    patientID: string;
};

export type GetDicomPatientStudiesQueryParams = {
    targetName: string;
};

export type GetDicomPatientStudiesError = Fetcher.ErrorWrapper<undefined>;

export type GetDicomPatientStudiesResponse = Schemas.DICOMPatientPresentation[];

export type GetDicomPatientStudiesVariables = {
    pathParams: GetDicomPatientStudiesPathParams;
    queryParams: GetDicomPatientStudiesQueryParams;
} & ConnectorContext['fetcherOptions'];

export const fetchGetDicomPatientStudies = (
    variables: GetDicomPatientStudiesVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        GetDicomPatientStudiesResponse,
        GetDicomPatientStudiesError,
        undefined,
        {},
        GetDicomPatientStudiesQueryParams,
        GetDicomPatientStudiesPathParams
    >({
        url: '/api/dicom/patients/{patientID}',
        method: 'get',
        ...variables,
        signal,
    });

export const useGetDicomPatientStudies = <
    TData = GetDicomPatientStudiesResponse,
>(
    variables: GetDicomPatientStudiesVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<
            GetDicomPatientStudiesResponse,
            GetDicomPatientStudiesError,
            TData
        >,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<
        GetDicomPatientStudiesResponse,
        GetDicomPatientStudiesError,
        TData
    >({
        queryKey: queryKeyFn({
            path: '/api/dicom/patients/{patientID}',
            operationId: 'getDicomPatientStudies',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetDicomPatientStudies(
                { ...fetcherOptions, ...variables },
                signal
            ),
        ...options,
        ...queryOptions,
    });
};

export type GetProxiedPathParams = {
    rest: string;
};

export type GetProxiedError = Fetcher.ErrorWrapper<undefined>;

export type GetProxiedVariables = {
    pathParams: GetProxiedPathParams;
} & ConnectorContext['fetcherOptions'];

/**
 * Proxies a GET request to the backend server.
 */
export const fetchGetProxied = (
    variables: GetProxiedVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        GetProxiedError,
        undefined,
        {},
        {},
        GetProxiedPathParams
    >({ url: '/proxy/{rest}', method: 'get', ...variables, signal });

/**
 * Proxies a GET request to the backend server.
 */
export const useGetProxied = <TData = undefined>(
    variables: GetProxiedVariables,
    options?: Omit<
        reactQuery.UseQueryOptions<undefined, GetProxiedError, TData>,
        'queryKey' | 'queryFn' | 'initialData'
    >
) => {
    const { fetcherOptions, queryOptions, queryKeyFn } =
        useConnectorContext(options);
    return reactQuery.useQuery<undefined, GetProxiedError, TData>({
        queryKey: queryKeyFn({
            path: '/proxy/{rest}',
            operationId: 'getProxied',
            variables,
        }),
        queryFn: ({ signal }) =>
            fetchGetProxied({ ...fetcherOptions, ...variables }, signal),
        ...options,
        ...queryOptions,
    });
};

export type PostProxiedPathParams = {
    rest: string;
};

export type PostProxiedError = Fetcher.ErrorWrapper<undefined>;

export type PostProxiedVariables = {
    pathParams: PostProxiedPathParams;
} & ConnectorContext['fetcherOptions'];

/**
 * Proxies a POST request to the backend server.
 */
export const fetchPostProxied = (
    variables: PostProxiedVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        PostProxiedError,
        undefined,
        {},
        {},
        PostProxiedPathParams
    >({ url: '/proxy/{rest}', method: 'post', ...variables, signal });

/**
 * Proxies a POST request to the backend server.
 */
export const usePostProxied = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            PostProxiedError,
            PostProxiedVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        PostProxiedError,
        PostProxiedVariables
    >({
        mutationFn: (variables: PostProxiedVariables) =>
            fetchPostProxied({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type PutProxiedPathParams = {
    rest: string;
};

export type PutProxiedError = Fetcher.ErrorWrapper<undefined>;

export type PutProxiedVariables = {
    pathParams: PutProxiedPathParams;
} & ConnectorContext['fetcherOptions'];

/**
 * Proxies a PUT request to the backend server.
 */
export const fetchPutProxied = (
    variables: PutProxiedVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        PutProxiedError,
        undefined,
        {},
        {},
        PutProxiedPathParams
    >({ url: '/proxy/{rest}', method: 'put', ...variables, signal });

/**
 * Proxies a PUT request to the backend server.
 */
export const usePutProxied = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            PutProxiedError,
            PutProxiedVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        PutProxiedError,
        PutProxiedVariables
    >({
        mutationFn: (variables: PutProxiedVariables) =>
            fetchPutProxied({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type DeleteProxiedPathParams = {
    rest: string;
};

export type DeleteProxiedError = Fetcher.ErrorWrapper<undefined>;

export type DeleteProxiedVariables = {
    pathParams: DeleteProxiedPathParams;
} & ConnectorContext['fetcherOptions'];

/**
 * Proxies a DELETE request to the backend server.
 */
export const fetchDeleteProxied = (
    variables: DeleteProxiedVariables,
    signal?: AbortSignal
) =>
    connectorFetch<
        undefined,
        DeleteProxiedError,
        undefined,
        {},
        {},
        DeleteProxiedPathParams
    >({ url: '/proxy/{rest}', method: 'delete', ...variables, signal });

/**
 * Proxies a DELETE request to the backend server.
 */
export const useDeleteProxied = (
    options?: Omit<
        reactQuery.UseMutationOptions<
            undefined,
            DeleteProxiedError,
            DeleteProxiedVariables
        >,
        'mutationFn'
    >
) => {
    const { fetcherOptions } = useConnectorContext();
    return reactQuery.useMutation<
        undefined,
        DeleteProxiedError,
        DeleteProxiedVariables
    >({
        mutationFn: (variables: DeleteProxiedVariables) =>
            fetchDeleteProxied({ ...fetcherOptions, ...variables }),
        ...options,
    });
};

export type QueryOperation =
    | {
          path: '/api/studies/{studyId}/series/{seriesId}.zip';
          operationId: 'downloadDicomSeries';
          variables: DownloadDicomSeriesVariables;
      }
    | {
          path: '/api/studies/{studyId}/series/{seriesId}/slices/{instanceId}.dcm';
          operationId: 'downloadDicomImage';
          variables: DownloadDicomImageVariables;
      }
    | {
          path: '/api/studies/{patientID}/{modality}/{sopInstanceUID}.dcm';
          operationId: 'downloadDicomImageBySOP';
          variables: DownloadDicomImageBySOPVariables;
      }
    | {
          path: '/api/patients';
          operationId: 'getPatients';
          variables: GetPatientsVariables;
      }
    | {
          path: '/api/patients/{patientId}';
          operationId: 'getPatient';
          variables: GetPatientVariables;
      }
    | {
          path: '/api/dicom/echo';
          operationId: 'echo';
          variables: EchoVariables;
      }
    | {
          path: '/api/dicom/targets';
          operationId: 'listTargets';
          variables: ListTargetsVariables;
      }
    | {
          path: '/api/session/{sessionID}';
          operationId: 'getSession';
          variables: GetSessionVariables;
      }
    | {
          path: '/api/patients/{patientId}/workflows/{workflowID}';
          operationId: 'getWorkflow';
          variables: GetWorkflowVariables;
      }
    | {
          path: '/api/tasks';
          operationId: 'getTasksList';
          variables: GetTasksListVariables;
      }
    | {
          path: '/api/tasks/{taskID}';
          operationId: 'getTaskStatus';
          variables: GetTaskStatusVariables;
      }
    | {
          path: '/api/tasks/{taskID}/results/{resultPath}';
          operationId: 'getTaskResult';
          variables: GetTaskResultVariables;
      }
    | {
          path: '/api/tasks/{taskID}/errors/{errorPath}';
          operationId: 'getTaskError';
          variables: GetTaskErrorVariables;
      }
    | {
          path: '/api/dicom/patients';
          operationId: 'getDicomPatients';
          variables: GetDicomPatientsVariables;
      }
    | {
          path: '/api/dicom/patients/{patientID}';
          operationId: 'getDicomPatientStudies';
          variables: GetDicomPatientStudiesVariables;
      }
    | {
          path: '/proxy/{rest}';
          operationId: 'getProxied';
          variables: GetProxiedVariables;
      };
